import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Global ATC interceptor for ui-product-card forms.
 *
 * The ui-product-card snippet emits a native HTML form for single-variant
 * products (action="/cart/add", method="post"). Without intervention the
 * browser submits and redirects to /cart on success. This module hijacks
 * that submission anywhere on the storefront — homepage carousels,
 * collection grids, search results, recently-viewed strips, cart upsell
 * strip — and routes it through the AJAX cart-add flow so the cart drawer
 * (or cart bubble in page-mode) updates in place.
 *
 * Architectural notes:
 *   - Single document-level listener (no per-card wiring; works for cards
 *     added dynamically via predictive search, infinite scroll, recs API).
 *   - Double-gate selector — action$="/cart/add" AND .product-card__form —
 *     so PDP <product-form-component>, quick-add modals, gift-card forms,
 *     and any other /cart/add forms outside ui-product-card are untouched.
 *   - Multi-variant cards render as <a href> in the snippet; they have no
 *     form, never reach this listener, and continue to navigate to PDP.
 *   - Sold-out variants render as disabled <button> with no <form> wrapper;
 *     never submit, never reach this listener.
 *   - The dispatched CartAddEvent re-uses the existing Wonder machinery:
 *     <cart-drawer-component> auto-opens (when settings.auto_open_cart_drawer
 *     is true), <cart-items-component> morphs the line-items section in
 *     place, <cart-upsell-component> re-hydrates with new recommendations.
 */

const FORM_SELECTOR = 'form.product-card__form[action$="/cart/add"]';

/**
 * Fire the native <fly-to-cart> animation from the product card's image
 * to the header cart icon. Mirrors product-form.js#animateFlyToCart so
 * the visual is consistent with PDP ATC. Fire-and-forget — the element
 * removes itself when the animation completes. Skips silently if any
 * required reference is missing (placeholder cards, headers without a
 * cart icon, etc.).
 *
 * @param {HTMLFormElement} form
 * @param {HTMLElement | null} submitBtn
 */
function flyToCart(form, submitBtn) {
  const card = form.closest('.product-card');
  const img = card?.querySelector('.product-card__img--primary');
  const imageUrl = img instanceof HTMLImageElement ? (img.currentSrc || img.src) : null;
  const cartIcon = document.querySelector('.header-actions__cart-icon');

  if (!imageUrl || !cartIcon || !submitBtn) return;

  const flyer = /** @type {any} */ (document.createElement('fly-to-cart'));
  flyer.classList.add('fly-to-cart--main');
  flyer.style.backgroundImage = `url(${imageUrl})`;
  flyer.style.setProperty('--start-opacity', '0');
  flyer.source = submitBtn;
  flyer.destination = cartIcon;

  document.body.appendChild(flyer);
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.matches(FORM_SELECTOR)) return;

  event.preventDefault();

  /** @type {HTMLButtonElement | null} */
  const submitBtn = form.querySelector('button[type="submit"]');

  // Fire the animation in parallel with the network request — flyer is
  // already mid-flight by the time the cart drawer opens.
  flyToCart(form, submitBtn);

  const formData = new FormData(form);

  // Append cart-items section ids so the /cart/add response includes
  // refreshed line-item HTML in a single round-trip.
  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      sectionIds.push(el.dataset.sectionId);
    }
  });
  if (sectionIds.length > 0) {
    formData.append('sections', sectionIds.join(','));
  }

  if (submitBtn instanceof HTMLButtonElement) submitBtn.disabled = true;

  const cfg = fetchConfig('javascript', { body: formData });
  const url = (window /** @type {any} */).Theme?.routes?.cart_add_url || '/cart/add.js';

  try {
    const response = await fetch(url, {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'application/javascript' },
    });
    const data = await response.json();

    if (data.status) {
      console.error('[product-card-ajax] add rejected', data.message);
      return;
    }

    const variantId = String(formData.get('id') || '');
    const productId = data.product_id ? String(data.product_id) : '';

    document.dispatchEvent(
      new CartAddEvent({}, form.id || 'product-card', {
        source: 'product-card',
        itemCount: 1,
        productId,
        variantId,
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error('[product-card-ajax] add failed', error);
  } finally {
    if (submitBtn instanceof HTMLButtonElement) submitBtn.disabled = false;
  }
});
